import { GlobeAltIcon, UserPlusIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { Link } from "@inertiajs/react";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

const BEATS = [
  {
    title: "Start with your product URL",
    detail: "That’s enough for us to discover key pages and evaluate them.",
    Icon: GlobeAltIcon,
  },
  {
    title: "Add a test user",
    detail: "We’ll sign in as that user, find the journeys available after they log in, and flag usability issues.",
    Icon: UserPlusIcon,
  },
] as const;

type Props = {
  open: boolean;
  onDismiss: () => void;
};

export default function ProductChangeExplainerDialog({ open, onDismiss }: Props) {
  const [overlayRoot, setOverlayRoot] = useState<HTMLElement | null>(null);

  useEffect(() => {
    setOverlayRoot(document.getElementById("layout-main-overlay"));
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onDismiss();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onDismiss]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const main = document.querySelector("main[scroll-region]");
    if (!(main instanceof HTMLElement)) {
      return;
    }

    const scrollTop = main.scrollTop;
    const previousOverflow = main.style.overflowY;
    main.style.overflowY = "hidden";
    main.scrollTop = scrollTop;

    return () => {
      main.style.overflowY = previousOverflow;
      main.scrollTop = scrollTop;
    };
  }, [open]);

  if (!open || !overlayRoot) {
    return null;
  }

  return createPortal(
    <div
      className="pointer-events-auto absolute inset-0 z-50 flex items-center justify-center overflow-y-auto p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="product-change-title"
    >
      <div className="absolute inset-0 bg-gray-900/20 backdrop-blur-sm" aria-hidden="true" />
      <div className="relative my-auto w-full max-w-xl overflow-hidden rounded-xl bg-white shadow-xl ring-1 ring-black/5">
        <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-6 py-4 sm:px-8 sm:py-5">
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              <GlobeAltIcon className="h-6 w-6" aria-hidden="true" />
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-primary">What’s changed</p>
              <h2 id="product-change-title" className="text-xl font-semibold tracking-tight text-gray-900 sm:text-2xl">
                Add a website — we’ll take it from there
              </h2>
            </div>
          </div>
          <button type="button" className="text-gray-400 hover:text-gray-600" onClick={onDismiss} aria-label="Close">
            <XMarkIcon className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <div className="px-6 py-5 sm:px-8 sm:py-6">
          <p className="text-sm leading-relaxed text-gray-600">
            You no longer set up custom tasks, start URLs, or personas. Add your product URL and we’ll find pages,
            browse them like a visitor, and flag usability issues.
          </p>
          <ul className="mt-5 space-y-5">
            {BEATS.map((beat, index) => (
              <li key={beat.title} className="flex gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <beat.Icon className="h-5 w-5" aria-hidden="true" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900">
                    <span className="mr-1.5 text-primary">{index + 1}.</span>
                    {beat.title}
                  </p>
                  <p className="mt-0.5 text-sm text-gray-600">{beat.detail}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="border-t border-gray-100 px-6 py-4 sm:px-8 sm:py-5">
          <Link href="/domains/new" className="btn btn-primary w-full justify-center sm:w-auto" onClick={onDismiss}>
            Add a website
          </Link>
        </div>
      </div>
    </div>,
    overlayRoot,
  );
}
